import { useState } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'

const HealthPhotoUpload = () => {
  const [file, setFile] = useState(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [userPrompt, setUserPrompt] = useState('')
  const [uploading, setUploading] = useState(false)
  const [analysis, setAnalysis] = useState(null)
  const [recordId, setRecordId] = useState(null)

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.type.startsWith('image/')) {
        toast.error('Please select an image file')
        return
      }
      
      // Validate file size (10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB')
        return
      }
      
      setFile(selectedFile)
    }
  }

  const uploadHealthPhoto = async () => {
    if (!file) {
      toast.error('Please select a photo first')
      return
    }

    setUploading(true)
    const formData = new FormData()
    formData.append('photo', file)
    formData.append('title', title || 'Health Photo Analysis')
    formData.append('description', description)
    formData.append('userPrompt', userPrompt)

    try {
      const response = await api.post('/health/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      console.log('Upload response:', response)
      toast.success('Photo uploaded successfully! Analysis in progress...')
      
      // Handle response structure (api.js returns response.data)
      const responseData = response.data || response
      setRecordId(responseData.id)
      
      // Poll for analysis results
      pollAnalysisStatus(responseData.id)

    } catch (error) {
      console.error('Upload error:', error)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      
      let errorMessage = 'Upload failed'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast.error(errorMessage)
    } finally {
      setUploading(false)
    }
  }

  const pollAnalysisStatus = async (id, attempts = 0) => {
    const maxAttempts = 30 // 30 seconds timeout
    
    try {
      const response = await api.get(`/health/${id}/status`)
      console.log('Status check response:', response)

      // Since api.js returns response.data, we need to handle accordingly
      const record = response.data || response
      
      if (record.status === 'completed') {
        setAnalysis(record.analysis)
        toast.success('Analysis completed!')
      } else if (record.status === 'failed') {
        toast.error('Analysis failed: ' + record.error)
      } else if (attempts < maxAttempts) {
        // Continue polling every 2 seconds
        setTimeout(() => pollAnalysisStatus(id, attempts + 1), 2000)
      } else {
        toast.error('Analysis timeout - please check back later')
      }
    } catch (error) {
      console.error('Status check error:', error)
      if (attempts < maxAttempts) {
        setTimeout(() => pollAnalysisStatus(id, attempts + 1), 2000)
      }
    }
  }

  const reset = () => {
    setFile(null)
    setTitle('')
    setDescription('')
    setUserPrompt('')
    setAnalysis(null)
    setRecordId(null)
  }

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
        Health Photo Analysis
      </h2>
      
      {!analysis ? (
        <div className="space-y-4 sm:space-y-6">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Health Photo
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            />
            {file && (
              <div className="mt-2 text-sm text-gray-600">
                Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title (Optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Skin mole check, Rash analysis..."
              className="input-field"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your health concern or what you'd like analyzed..."
              rows="3"
              className="input-field"
            />
          </div>

          {/* Custom Prompt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Custom Analysis Prompt (Optional)
            </label>
            <textarea
              value={userPrompt}
              onChange={(e) => setUserPrompt(e.target.value)}
              placeholder="Specific instructions for the AI analysis..."
              rows="2"
              className="input-field"
            />
          </div>

          {/* Upload Button */}
          <button
            onClick={uploadHealthPhoto}
            disabled={!file || uploading}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? 'Analyzing...' : 'Upload & Analyze Photo'}
          </button>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Disclaimer:</strong> This analysis is for informational purposes only 
              and should not replace professional medical advice. Always consult with a 
              healthcare professional for proper medical evaluation and treatment.
            </p>
          </div>
        </div>
      ) : (
        /* Analysis Results */
        <div className="space-y-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-green-800 mb-2">
              Analysis Complete
            </h3>
            <p className="text-green-700">{analysis.summary}</p>
          </div>

          {/* Detailed Analysis */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Detailed Analysis</h4>
            <p className="text-gray-700">{analysis.details}</p>
          </div>

          {/* Concerns */}
          {analysis.concerns && analysis.concerns.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-semibold text-gray-900">Identified Concerns</h4>
              {analysis.concerns.map((concern, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${
                    concern.level === 'high'
                      ? 'bg-red-50 border-red-200 text-red-800'
                      : concern.level === 'medium'
                      ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                      : 'bg-blue-50 border-blue-200 text-blue-800'
                  }`}
                >
                  <span className="font-medium capitalize">{concern.level} Priority:</span>{' '}
                  {concern.description}
                </div>
              ))}
            </div>
          )}

          {/* Recommendations */}
          {analysis.recommendations && analysis.recommendations.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Recommendations</h4>
              <ul className="list-disc list-inside space-y-1">
                {analysis.recommendations.map((rec, index) => (
                  <li key={index} className="text-gray-700">{rec}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Confidence */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-900">Analysis Confidence</span>
              <span className="text-lg font-bold text-primary-600">
                {analysis.confidence}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
              <div
                className="bg-primary-600 h-2 rounded-full"
                style={{ width: `${analysis.confidence}%` }}
              ></div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <button
              onClick={reset}
              className="btn-secondary flex-1 w-full sm:w-auto"
            >
              Analyze Another Photo
            </button>
            <button
              onClick={() => {
                // Navigate to health records or save functionality
                console.log('View all records')
              }}
              className="btn-primary flex-1 w-full sm:w-auto"
            >
              View All Records
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default HealthPhotoUpload
